/**
 * eİcraPro kimliği imzacıya devredildi mi ("eİcraPro ile bağlantı sağlandı mı").
 *
 * DEV-2603 (DEV-3899/3901): bu fonksiyon artık yalnız HAM ÖNERİYİ (setConfig'in
 * sessionStorage'a yazdığı token + tenant_id) doğrular; "bağlı" bayrağını sürmez.
 * Bayrağın tek kaynağı `eicraproDurumu`dur (aşağıda): öneri sunucudaki lisansla
 * doğrulanıp AKTİF KİMLİK olunca 'tamam' olur (bkz. utils/aktifKimlik.ts).
 *
 * Kimliği tarayıcıdaki eİcraPro yazar: `setApiCookiesToImza()` Electron
 * proxy'sinin `/setConfig` ucuna token + tenant + tckn + apiType gönderir; proxy
 * bunları bu pencerenin sessionStorage'ına yazar ve API hedefini (proxyTarget)
 * aynı çağrıda belirler, ardından socket'e `updateToken` yayınlanır. Devir
 * olmadan imzacı:
 *  - API'ye istek ATMAMALI: proxy'nin API hedefi boştur ("Host should not be
 *    empty", DEV-2603 9 Eki yorumu), hedef dolu olsa bile Authorization ve
 *    Tenant-Id yoktur;
 *  - UYAP'a GİRMEMELİ: hesap TCKN'si bilinmediği için hesap-kart kimlik
 *    denetimi atlanır ve oturum kime ait olduğu bilinmeden açılır.
 *
 * Şart: token ve tenant_id dolu (Laravel TenantValidationMiddleware tenant_id
 * başlığı olmadan 401 döner). Ham TCKN şart DEĞİL (öneri aşamasında); asıl TCKN
 * kuralı lisans sorgusundadır: lisans sahibinin TCKN'si yoksa kimlik aktif olmaz.
 * eİcraPro'nun store'u boşken gönderdiği "Bearer undefined" gibi değerler devir
 * sayılmaz.
 *
 * Saf tutuldu (depo dışarıdan verilir) ki test edilebilsin. Vuex getter'ı
 * içinde ÇAĞRILMAMALI: sessionStorage reaktif değildir, getter ilk değerde
 * donar.
 */
export function eicraproKimligiVarMi(depo: Pick<Storage, 'getItem'> | null | undefined): boolean {
    if (!depo) {
        return false;
    }
    let token: string | null;
    let tenantId: string | null;
    try {
        token = depo.getItem('accessToken');
        tenantId = depo.getItem('tenant_id');
    } catch (e) {
        return false;
    }
    const tokenGovdesi = String(token ?? '').trim().replace(/^Bearer\s*/i, '');
    return doluMu(tokenGovdesi) && doluMu(String(tenantId ?? '').trim());
}

function doluMu(deger: string): boolean {
    return deger !== '' && deger !== 'undefined' && deger !== 'null';
}

/**
 * Kimlik gelmeden UYAP girişi istendiğinde `connectToUyap`'ın fırlattığı hatanın
 * metni. Çağıranlar bu hatayı "başarısız giriş" değil "bekleme" olarak tanır.
 */
export const EICRAPRO_BEKLENIYOR_MESAJI =
    "eİcraPro bağlantısı bekleniyor. Tarayıcıdan eİcraPro'ya giriş yaptığınızda UYAP bağlantısı kendiliğinden kurulur.";

/**
 * İmzacının eİcraPro kimlik durumu (DEV-3901):
 *  - 'yok': token/büro henüz yok (ya da token geçersizleşti); eİcraPro'dan devir bekleniyor.
 *  - 'tckn-eksik': öneri geldi ama lisans doğrulaması kimliği reddetti: lisans sahibinin
 *    TCKN'si kayıtlı değil ya da büroda etkin eİcraPro lisansı yok. UYAP girişi/bot YOK.
 *  - 'tamam': aktif kimlik var, geçerli.
 */
export type EicraproDurumu = 'yok' | 'tckn-eksik' | 'tamam';

/** Öneri neden reddedildi: lisans sahibinin TCKN'si yok / büroda etkin lisans yok. */
export type KimlikRedSebebi = 'tckn-eksik' | 'lisans-yok';

export const EICRAPRO_TCKN_EKSIK_MESAJI =
    'Bu büronun eİcraPro lisansında TC kimlik numarası kayıtlı değil. UYAP girişi için destek ile iletişime geçin.';

export const EICRAPRO_LISANS_YOK_MESAJI = 'Bu büroda etkin eİcraPro lisansı bulunamadı.';

/** aktifGecerli: aktif kimlik var ve token'ı geçersizleşmemiş. */
export function eicraproDurumunuBelirle(aktifGecerli: boolean, red: KimlikRedSebebi | null): EicraproDurumu {
    if (aktifGecerli) {
        return 'tamam';
    }
    return red ? 'tckn-eksik' : 'yok';
}

export function eicraproDurumuMesaji(durum: EicraproDurumu, red: KimlikRedSebebi | null): string {
    if (durum === 'tamam') {
        return '';
    }
    if (durum === 'tckn-eksik') {
        return red === 'lisans-yok' ? EICRAPRO_LISANS_YOK_MESAJI : EICRAPRO_TCKN_EKSIK_MESAJI;
    }
    return EICRAPRO_BEKLENIYOR_MESAJI;
}

/**
 * `connectToUyap` kapısının hatası mı: bekleme ('yok'), TCKN eksik ya da lisans yok.
 * Üçü de "başarısız giriş" değil, kimlik tamamlanana kadar BEKLEMEdir.
 */
export function eicraproBekleniyorHatasiMi(hata: unknown): boolean {
    const mesaj = (hata as { message?: unknown } | null | undefined)?.message;
    return mesaj === EICRAPRO_BEKLENIYOR_MESAJI ||
        mesaj === EICRAPRO_TCKN_EKSIK_MESAJI ||
        mesaj === EICRAPRO_LISANS_YOK_MESAJI;
}
